import { useCallback, useEffect, useMemo, useState } from 'react'
import { FetchState } from 'shared/api'
import { flatten, maxText } from 'shared/util'
import { useKeyBind } from 'shared/keyBind'

import { useEvaluationForm, useSearch, useTargetList } from 'features/search/context'
import { useEdittableKojinList } from 'features/kojin/context/list'

import { KojinResult, useGetKojin } from 'interface/kojin'
import { KojinAnnotateResult } from 'interface/kojin/annotate'
import { AuthorResult, useEditAuthor, useGetAuthor } from 'interface/author'
import { ArticleResult } from 'interface/article'
import { TrialResult } from 'interface/trial'

import { useSisetuPreviewDialog } from 'features/sisetu/dialog/preview/hooks'
import { useKojinPreviewDialog } from 'features/kojin/dialog/preview/hooks'
import { useArticlePreviewDialog } from 'features/article/dialog/preview/hooks'
import { useTrialPreviewDialog } from 'features/trial/dialog/preview/hooks'
import { useKojinPrint } from 'features/kojin/report/container'
import { SisetuResult } from 'interface/sisetu'
import { useListEvaluationResultsByKojin } from 'interface/search/evaluation'
import { evaluationSummary } from 'interface/search/evaluation/util'

type useKojinPreviewProps = {
  kojinList?: KojinResult[]
  defaultKojinCode?: string | null
	defaultKojin?: KojinResult | null
  defaultAuthorId?: number | null
	defaultAuthor?: AuthorResult | null
	onSisetuOpenReply?: (sisetuCode: string | null) => void
  onKojinOpenReply?: (kojinCode: string | null) => void
  onAuthorOpenReply?: (authorId: number | null) => void
  onArticleOpenReply?: (articleId: number | null) => void
	onTrialOpenReply?: (trialId: number | null) => void
	onChangeEvaluation?: (kojinCode: string) => void
  onClose?: () => void
}
export const useKojinPreview = ({
	kojinList,
	defaultKojinCode,
	defaultKojin,
	defaultAuthorId,
	defaultAuthor,
	onSisetuOpenReply,
	onKojinOpenReply,
	onAuthorOpenReply,
	onArticleOpenReply,
	onTrialOpenReply,
	onChangeEvaluation,
	onClose
}: useKojinPreviewProps) => {
	const search = useSearch()
	const evaluationForm = useEvaluationForm()
	const targetList = useTargetList()

  const [modal, setModal] = useState<'download' | null>(null)
	const [kojinCode, setKojinCode] = useState<string | null>(defaultKojinCode ?? defaultKojin?.kojinCode ?? null)
	const [authorId, setAuthorId] = useState<number | null>(defaultAuthorId ?? defaultKojin?.authorId ?? null)

	const {
		openKojin, kojinCode: selectedKojinCode,
		openAuthor, authorId: selectedAuthorId
	} = useKojinPreviewDialog()
	const {
		openSisetu, sisetuCode: selectedSisetuCode,
		setSisetuList, sisetuList
	} = useSisetuPreviewDialog()
	const {
		openArticle, articleId: selectedArticleId,
		setArticleList, articleList
	} = useArticlePreviewDialog()
	const {
		openTrial, trialId: selectedTrialId,
		setTrialList, trialList
	} = useTrialPreviewDialog()

	const {
		current,
		prev,
		next,
	} = useMemo(
		() => {
			const index = (
				kojinCode ? (kojinList?.findIndex(kojin => kojin.kojinCode === kojinCode) ?? -1) :
				authorId ? (kojinList?.findIndex(kojin => kojin.authorId === authorId) ?? -1) :
				-1
			)
			return {
				current: (kojinList && index >= 0) ? kojinList[index] : null,
				next: (kojinList && index >= 0 && index < (kojinList.length - 1)) ? kojinList[index + 1] : null,
				prev: (kojinList && index > 0) ? kojinList[index - 1] : null
			}
		},
		[kojinList, kojinCode, authorId]
	)

	const kojinState: FetchState<KojinResult> = useGetKojin(
		search?.searchId,
		targetList?.listId,
		kojinCode,
		{
			defaultKojin: current ?? defaultKojin ?? undefined,
			updateTime: maxText(targetList?.updateTime, search?.updateTime) ?? undefined
		}
	)
  const kojin = useMemo(() => kojinState.data, [kojinState])

	const authorState = useGetAuthor(
		search?.searchId,
		authorId,
		{
			defaultAuthor: defaultAuthor ?? undefined,
			updateTime: search?.updateTime
		}
	)
  const author = useMemo(() => authorState.data, [authorState])

	useEffect(
		() => {
			if (kojin) {
				setKojinCode(kojin.kojinCode ?? null)
				setAuthorId(kojin.authorId ?? null)
			} else if (author) {
				setAuthorId(author.authorId ?? null)
			}
		},
		[kojin, author, setKojinCode, setAuthorId]
	)

	const articleCount = useMemo(
    () => (
      kojin?.score?.journal?.counts?.total ??
      author?.counts?.total ??
			0
    ),
    [kojin, author]
  )

	const evaluationResultsState = useListEvaluationResultsByKojin(search?.searchId, kojin?.kojinCode)
	const evaluationResultSummary = useMemo(
		() => evaluationSummary(evaluationForm, evaluationResultsState.data?.list ?? []),
		[evaluationResultsState, evaluationForm]
	)
	const onEditEvaluation = useCallback(
		() => {
			evaluationResultsState.refetch()
			if (onChangeEvaluation && kojinCode) {
				onChangeEvaluation(kojinCode)
			}
		},
		[evaluationResultsState, onChangeEvaluation, kojinCode]
	)

	const annotate = useMemo(
    () => kojin?.annotate || null,
    [kojin]
  )

  const edit = useEdittableKojinList()
	const [postCandidateState, postCandidate] = useEditAuthor(
		search?.searchId, targetList?.listId, author?.authorId, {
			onEditted: (result) => {
				const authorId = author?.authorId
				if (!authorId || !edit?.setCandidate) return
				edit.setCandidate(authorId, result)
			}
		}
	)
  const onChangeAnnotate = useCallback(
    (kojinCode: string, annotate: KojinAnnotateResult) => {
      if (edit?.setAnnotate) edit.setAnnotate(kojinCode, annotate)
    },
    [edit]
  )

	const closeModal = useCallback(
		(exclusive: 'kojin' | 'sisetu' | 'article' | 'trial' | null = null) => {
			setModal(null)
			if (exclusive !== 'sisetu') {
				if (onSisetuOpenReply) {
					onSisetuOpenReply(null)
				} else {
					openSisetu(null)
				}
			}
			if (exclusive !== 'kojin') {
				if (onKojinOpenReply) {
					onKojinOpenReply(null)
				} else {
					openKojin(null)
				}
			}
			if (exclusive !== 'article') {
				if (onArticleOpenReply) {
					onArticleOpenReply(null)
				} else {
					openArticle(null)
				}
			}
			if (exclusive !== 'trial') {
				if (onTrialOpenReply) {
					onTrialOpenReply(null)
				} else {
					openTrial(null)
				}
			}
		},
		[setModal, onSisetuOpenReply, openSisetu, onKojinOpenReply, openKojin, openArticle, onArticleOpenReply, openTrial, onTrialOpenReply]
	)

	const isShowModal = useMemo(
		() => !!(selectedKojinCode || selectedSisetuCode || selectedAuthorId || selectedArticleId || selectedTrialId),
		[selectedKojinCode, selectedSisetuCode, selectedAuthorId, selectedArticleId, selectedTrialId]
	)

	const onKojinDownloadOpen = useCallback(
		() => {
			setModal('download')
		},
		[setModal]
	)
	const onSisetuOpen = useCallback(
		(sisetuCode: string | null, list?: SisetuResult[]) => {
			closeModal('sisetu')
			if (onSisetuOpenReply) {
				onSisetuOpenReply(sisetuCode)
			} else {
				setSisetuList(list ?? [])
				openSisetu(sisetuCode)
			}
		},
		[closeModal, onSisetuOpenReply, openSisetu, setSisetuList]
	)
	const onKojinOpen = useCallback(
		(otherKojinCode: string | null) => {
			closeModal('kojin')
			if (onKojinOpenReply) {
				onKojinOpenReply(otherKojinCode)
			} else {
				setKojinCode(otherKojinCode === kojinCode ? null : otherKojinCode)
				setAuthorId(null)
			}
		},
		[closeModal, onKojinOpenReply, kojinCode, setKojinCode, setAuthorId]
	)
	const onAuthorOpen = useCallback(
		(otherAuthorId: number | null) => {
			closeModal('kojin')
			if (onAuthorOpenReply) {
				onAuthorOpenReply(otherAuthorId)
			} else {
				setKojinCode(null)
				setAuthorId(otherAuthorId === authorId ? null : otherAuthorId)
			}
		},
		[closeModal, onAuthorOpenReply, authorId, setKojinCode, setAuthorId]
	)
	const onOtherKojinOpen = useCallback(
		(otherKojinCode: string | null) => {
			closeModal('kojin')
			openKojin(otherKojinCode === kojinCode ? null : otherKojinCode)
		},
		[closeModal, kojinCode, openKojin]
	)
	const onOtherAuthorOpen = useCallback(
		(otherAuthorId: number | null) => {
			closeModal('kojin')
			openAuthor(otherAuthorId === authorId ? null : otherAuthorId)
		},
		[closeModal, authorId, openAuthor]
	)
	const onArticleOpen = useCallback(
		(article: ArticleResult | null, list?: ArticleResult[]) => {
			closeModal('article')
			setArticleList(list ?? [])
			openArticle(article?.articleId ?? null)
		},
		[closeModal, setArticleList, openArticle]
	)
	const onTrialOpen = useCallback(
		(trial: TrialResult | null, list?: TrialResult[]) => {
			closeModal('trial')
			setTrialList(list ?? [])
			openTrial(trial?.trialId ?? null)
		},
		[closeModal, setTrialList, openTrial]
	)

	const openPrev = useCallback(
		() => {
			if (isShowModal) return
			if (prev?.kojinCode) onKojinOpen(prev.kojinCode)
			else if (prev?.authorId) onAuthorOpen(prev.authorId)
		},
		[prev, onKojinOpen, onAuthorOpen, isShowModal]
	)
	const openNext = useCallback(
		() => {
			if (isShowModal) return
			if (next?.kojinCode) onKojinOpen(next.kojinCode)
			else if (next?.authorId) onAuthorOpen(next.authorId)
		},
		[next, onKojinOpen, onAuthorOpen, isShowModal]
	)

	const download = useKojinPrint({
		search, kojin,
		onDownloadEnd: closeModal
	})

	useKeyBind({ key: "PageUp", onKeyDown: openPrev })
	useKeyBind({ key: "PageDown", onKeyDown: openNext })

	return {
		search,
		loading: kojinState.loading || authorState.loading || evaluationResultsState.loading || postCandidateState.posting,
		error: flatten([postCandidateState.error, kojinState.error, authorState.error, evaluationResultsState.error]),
		kojinCode, authorId,
		kojin, author,
		articleCount, annotate,
		evaluationForm,
		evaluationResultSummary,
		hasPrev: prev !== null ? true : false,
		hasNext: next !== null ? true : false,
		openPrev, openNext,
		onChangeAnnotate: edit ? onChangeAnnotate : undefined,
		onSelectCandidate: edit ? (kojinCode: string | null) => postCandidate({ kojinCode }) : undefined,
		modal, closeModal,
		onKojinDownloadOpen,
		onSisetuOpen, selectedSisetuCode, sisetuList,
		onOtherKojinOpen, selectedKojinCode,
		onOtherAuthorOpen, selectedAuthorId,
		onArticleOpen, selectedArticleId, articleList,
		onTrialOpen, selectedTrialId, trialList,
		downloading: download.downloading,
		onDownload: download.onDownload,
		onEditEvaluation,
		onClose
	}
}
